import { z } from 'zod';
import { currencyCodeSchema, sortCurrencies } from './currency.js';
import { addMoney, exchangeRateSchema, formatMoney, isZeroMoney, moneyAmountSchema, toBaseAmount, } from './money.js';
/** Ledger account types (CLAUDE.md §4). Debit-positive: assets and expenses grow with + amounts. */
export const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'income', 'expense'];
export const ACCOUNT_SUBTYPES = [
    'cash',
    'bank',
    'wallet',
    'receivable',
    'payable',
    'loan',
    'card',
    'category',
    'fx_trading',
    'opening_balance',
];
/**
 * The kinds of money account people can create in Phase 2. Receivable, payable and loan arrive
 * with debts in Phase 4.
 */
export const MONEY_ACCOUNT_KINDS = ['cash', 'bank', 'wallet', 'card'];
export const moneyAccountKindSchema = z.enum(MONEY_ACCOUNT_KINDS);
export function accountTypeOfKind(kind) {
    return kind === 'card' ? 'liability' : 'asset';
}
export const accountNameSchema = z.string().trim().min(1).max(80);
/** One currency of an account and its balance (debit-positive, like `balance`). */
export const currencyBalanceSchema = z.object({
    currency: currencyCodeSchema,
    balance: moneyAmountSchema,
});
/**
 * A total in the family's base currency. When `estimated` is true, some of it was converted at
 * the latest family rate and will move when the rate changes (screens say "≈ ... at today's
 * rate"). `missingRates` lists currencies left out because no rate was ever entered.
 */
export const baseTotalSchema = z.object({
    currency: currencyCodeSchema,
    amount: moneyAmountSchema,
    estimated: z.boolean(),
    missingRates: z.array(currencyCodeSchema),
});
/**
 * A money account as people see it. Since Phase 3 one account can hold several currencies
 * ("My cash" with a USD and an LBP balance); in the ledger each currency is its own ledger
 * account, and everything below (owner, privacy, archive, permissions) applies to all of them.
 */
export const accountSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    kind: moneyAccountKindSchema,
    type: z.enum(['asset', 'liability']),
    /** The account's first currency; kept with `balance` for older clients. Use `balances`. */
    currency: currencyCodeSchema,
    /** The member this account belongs to; null for a shared family account. */
    ownerMemberId: z.string().uuid().nullable(),
    ownerDisplayName: z.string().nullable(),
    /** True when the account is currently private (marked private and its owner may have private accounts). */
    isPrivate: z.boolean(),
    archivedAt: z.string().nullable(),
    /**
     * Sum of postings, debit-positive: what you have for cash/bank/wallet. For a card a negative
     * balance is what you owe.
     */
    balance: moneyAmountSchema,
    /** Every currency the account holds, base currency first. */
    balances: z.array(currencyBalanceSchema).min(1),
    /** All balances together in the base currency. */
    baseTotal: baseTotalSchema,
    /** What the viewer may do with it (decided by the server's policy). */
    canManage: z.boolean(),
    canRecord: z.boolean(),
    createdAt: z.string(),
});
export const accountListResponseSchema = z.object({ items: z.array(accountSchema) });
export const createAccountRequestSchema = z.object({
    name: accountNameSchema,
    kind: moneyAccountKindSchema,
    /** The account's first currency; defaults to the family's base currency. */
    currency: currencyCodeSchema.optional(),
    /** Whose account this is; null makes a shared family account. */
    ownerMemberId: z.string().uuid().nullable(),
    /**
     * Starting balance in everyday terms: money you have for cash/bank/wallet, money you owe for a
     * card. Zero or absent records nothing.
     */
    openingBalance: moneyAmountSchema.optional(),
    /**
     * For an opening balance in another currency than the base: a one-off rate (1 base = X).
     * Without it the family rate in effect today is used.
     */
    openingRate: exchangeRateSchema.optional(),
    isPrivate: z.boolean().default(false),
});
export const updateAccountRequestSchema = z
    .object({
    name: accountNameSchema,
    isPrivate: z.boolean(),
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, 'Nothing to update');
export const archiveAccountRequestSchema = z
    .object({
    /** Must be true to archive an account whose balance is not zero. */
    confirmNonZeroBalance: z.boolean().default(false),
})
    .default({ confirmNonZeroBalance: false });
/** Adds a currency to an account (or brings back one that was removed). */
export const addAccountCurrencyRequestSchema = z.object({
    currency: currencyCodeSchema,
    /** Starting balance in that currency, in everyday terms (see createAccountRequestSchema). */
    openingBalance: moneyAmountSchema.optional(),
    /** One-off rate for the opening balance; defaults to the family rate in effect today. */
    openingRate: exchangeRateSchema.optional(),
});
/** Adds amounts up per currency (never mixing currencies), base currency first. */
export function sumByCurrency(rows, base) {
    const sums = new Map();
    for (const r of rows)
        sums.set(r.currency, addMoney(sums.get(r.currency) ?? '0', r.amount));
    return sortCurrencies([...sums.keys()], base).map((currency) => ({
        currency,
        balance: formatMoney(sums.get(currency)),
    }));
}
/**
 * Adds balances in several currencies up in the base currency: base-currency balances as they
 * are, others converted with the latest family rate (1 base = rate), each with toBaseAmount.
 * A non-zero balance in a currency with no rate is left out and listed in `missingRates`.
 */
export function estimateBaseTotal(balances, baseCurrency, latestRates) {
    const parts = [];
    const missing = new Set();
    let estimated = false;
    for (const { currency, balance } of balances) {
        if (currency === baseCurrency) {
            parts.push(balance);
            continue;
        }
        if (isZeroMoney(balance))
            continue;
        const rate = latestRates[currency];
        if (rate === undefined) {
            missing.add(currency);
            continue;
        }
        parts.push(toBaseAmount(balance, rate));
        estimated = true;
    }
    return {
        currency: baseCurrency,
        amount: addMoney('0', ...parts),
        estimated,
        missingRates: [...missing].sort(),
    };
}
//# sourceMappingURL=accounts.js.map